import { Certificate } from '../../../apis/api-client';
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '../../ui';
import { CertificateIcon, DownloadIcon } from '../../icons';
import { formatDate } from '../../../utils/roleHelpers';

export interface LearnerActivityCertificatesProps {
  certificates: Certificate[];
  loading: boolean;
  /** Id of the certificate whose download is in flight (spinner). */
  downloadingId: number | null;
  onDownload: (cert: Certificate) => void;
}

/**
 * LearnerActivityCertificates
 *
 * Certificates tab of the learner "Your activity" panel — a flat list
 * of earned certificates with per-row download buttons. Presentational;
 * extracted from LearnerActivitySummary.
 */
export default function LearnerActivityCertificates({
  certificates,
  loading,
  downloadingId,
  onDownload,
}: LearnerActivityCertificatesProps) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (certificates.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
        No certificates earned yet — finish a course that issues one to see it here.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {certificates.map((cert) => (
        <Box
          key={cert.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'primary.main',
              boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
            },
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              bgcolor: 'rgba(232,245,233,1)',
              color: 'success.dark',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <CertificateIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {cert.courseTitle || `Course #${cert.courseId}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Issued{' '}
              {formatDate(cert.createdAt ? new Date(cert.createdAt as any) : undefined)}
              {cert.completedAt &&
                ` · Completed ${formatDate(new Date(cert.completedAt as any))}`}
            </Typography>
          </Box>
          <Tooltip title="Download certificate">
            <IconButton
              size="small"
              onClick={() => onDownload(cert)}
              disabled={downloadingId === cert.id}
            >
              {downloadingId === cert.id ? (
                <CircularProgress size={18} />
              ) : (
                <DownloadIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      ))}
    </Box>
  );
}
